import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  AnnounceContext,
  type Announce,
} from "@/blueprint/components/LiveAnnouncer/useAnnounce";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";

const ANNOUNCEMENT_GAP_MS = 50;
const CLEAR_AFTER_MS = 5000;

type LiveAnnouncerProps = {
  children: ReactNode;
};

const LiveAnnouncer = ({ children }: LiveAnnouncerProps) => {
  const [message, setMessage] = useState("");
  const [queue, setQueue] = useState<string[]>([]);
  const lastMessageRef = useRef<string | null>(null);

  const announce = useCallback<Announce>((nextMessage) => {
    if (nextMessage.length === 0 || lastMessageRef.current === nextMessage) {
      return;
    }

    lastMessageRef.current = nextMessage;
    setQueue((current) => [...current, nextMessage]);
  }, []);

  useEffect(() => {
    const nextMessage = queue[0];
    if (nextMessage === undefined) {
      return;
    }

    // Clearing first makes queued updates observable even when assistive
    // technology coalesces multiple polite-region mutations.
    setMessage("");
    const timeoutId = window.setTimeout(() => {
      setMessage(nextMessage);
      setQueue((current) => current.slice(1));
    }, ANNOUNCEMENT_GAP_MS);

    return () => window.clearTimeout(timeoutId);
  }, [queue]);

  useEffect(() => {
    if (message.length === 0 || queue.length > 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setMessage("");
      lastMessageRef.current = null;
    }, CLEAR_AFTER_MS);

    return () => window.clearTimeout(timeoutId);
  }, [message, queue.length]);

  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {message}
      </VisuallyHidden>
    </AnnounceContext.Provider>
  );
};

export default LiveAnnouncer;
